/**
 * Shared state-resolution + visual helpers for the per-row Issues
 * surface. Both ``IssuesChip`` (the right-frozen table chip) and
 * ``ExceptionDrawer`` (the row-scoped overlay) consume this module so
 * there is exactly one source of truth for: which lifecycle bucket a
 * row falls into, what label/icon/colour belongs to that bucket, and
 * how the chip's sx block is composed.
 *
 * State table (the chip's MAIN state is lifecycle-only):
 *   errors non-empty + any blocked     -> blocked      (red filled + Block icon)
 *   errors non-empty + none blocked    -> needs_review (orange filled + Warning icon)
 *   errors empty + recently-resolved   -> resolved     (teal outlined + Check icon)
 *   errors empty + only ignored        -> ignored      (grey outlined + VisibilityOff icon)
 *   nothing                            -> empty        (muted secondary chip rendered by IssuesChip)
 *
 * Marked-fixed is NOT a lifecycle status: it renders as secondary PROGRESS
 * metadata, never a peer pill/chip. ``fixMarker`` is the single source for its
 * state + who/when hover; the drawer row renders that as a borderless "Marked
 * fixed · {state}" indicator (teal assignment-tick awaiting, red
 * assignment-warning once a later sync still saw it - drawer-local copy, see
 * ``IssuesEntryRow``), while the table chip and ``fixMarker.detail`` keep the
 * compact labels ``AWAITING_SYNC_DETAIL`` = "Pending next sync" /
 * ``FIX_NOT_CONFIRMED_DETAIL`` = "Failed check"; the full explanation lives in
 * the tooltip. In the table
 * (``tableFixMarker``) it's a small corner pennant on the chip (top-left,
 * mirroring the note badge), sourcing its icon + colour from ``FIX_STATE_META``
 * so it never widens the Issues column - teal ``AssignmentTurnedIn`` awaiting,
 * red ``AssignmentLate`` not-confirmed.
 * Lifecycle (pill / rail / chip) never recolours for it. Green/check stays
 * reserved for sync-confirmed resolved only; the mark-fixed ACTION affordances
 * (kebab item, "I've fixed all" button) keep teal (``MARKED_FIXED_FG``).
 */
import React from 'react'
import BlockIcon from '@mui/icons-material/Block'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate'
import type { SvgIconComponent } from '@mui/icons-material'
import type { ReferenceLink } from '../../../components/shared/referenceLinks/types'
import type { ExceptionDrawerDetailBlock } from '../types'
import { getDaysOpen } from '../../../utils/inspectorFormatters'
import { formatRelativeTimestamp } from '../../../utils/dateFormatters'

export type IssuesChipEntry = {
  code: string
  label: string
  primary?: boolean
  blocked?: boolean
  block_reason?: string
  // Who set the block + when. The status-detail line shows "Blocked reason:
  // {reason}" with a hover of "Blocked by {name} {ago}".
  blocked_by?: string | null
  blocked_at?: string | null
  links?: ReferenceLink[]
  // Closed-side only: which lifecycle bucket the entry belongs to.
  kind?: 'resolved' | 'ignored'
  // Backend-built "Why this was flagged" blocks for this entry. Eager on
  // the current row (phase 3a); the lazy by-tag fetch fills siblings (3b).
  detail_blocks?: ExceptionDrawerDetailBlock[]
  // The entry's lifecycle tag id - keys the lazy by-tag detail fetch that
  // fills a sibling row's Why + How on expand (phase 3b).
  tag_id?: number
  // Weight-0 roll-up (the sales reconciliation umbrella). The drawer shows
  // it as a headline only - never an actionable open-issue row.
  is_umbrella?: boolean
  // Business classification (process_issue | data_discrepancy) - the quiet type
  // lead shown at the top of the expanded body. Keys EXCEPTION_TYPE_META.
  classification?: string | null
  // Open entry already marked fixed (awaiting sync confirmation) - surfaced as
  // a secondary progress marker (see ``fixMarker``), never as a lifecycle chip.
  marked_fixed?: boolean
  // How a marked-fixed OPEN entry stands against the latest sync sweep.
  // Derived once backend-side (lifecycle.fix_confirmation_state); absent
  // when unmarked.
  fix_confirmation_state?: 'awaiting_sync' | 'still_detected'
  // Display name of who marked the entry fixed (never an email).
  marked_fixed_by?: string | null
  // The actor's team ("Evo" / "PSC"), shown in brackets after their name.
  // Derived backend-side from the actor's role; absent for product-path rows.
  marked_fixed_by_team?: string | null
  // When the entry was marked fixed (ISO) - feeds the marker tooltip's
  // "{time} ago". Absent when unmarked.
  marked_fixed_at?: string | null
  // Open-side freshness timestamps - feed the tooltip's "Oldest open" line.
  detected_at?: string | null
  last_detected_at?: string | null
  // Closed-side timestamps (one set per the immutable-rows-per-cycle
  // model) - feed the closed tooltip's "Last resolved" line.
  resolved_at?: string | null
  ignored_at?: string | null
  // Who set it ignored + the reason. The status-detail line shows "Ignored
  // reason: {reason}" with a hover of "Ignored by {name} {ago}".
  ignored_by?: string | null
  ignore_reason?: string | null
  // Outlet name when the entry's tag carries a composite ``product_id:outlet_id``
  // object_id (per-outlet stock codes); absent otherwise. Set by the backend in
  // prefix-rollup (drawer) mode - drives the grouped "· N outlets" breakdown.
  outlet_name?: string | null
  // --- Synthetic fields stamped by ``groupEntriesByCode`` when several same-code
  // entries (one per outlet) collapse into one representative row. Absent (or 1)
  // on a single-occurrence code, so the row renders exactly as before. ---
  // How many outlet occurrences the group folds (>1 only on a multi-outlet rollup).
  outlet_count?: number
  // The affected outlet names, for the expanded breakdown list.
  outlet_names?: string[]
  // Every member tag id - the grouped row's kebab acts on all of them at once.
  member_tag_ids?: number[]
}

/**
 * A source deep-link for a row (Lightspeed sale/ledger/product, Xero invoice,
 * Skimmer invoice). Built centrally by the backend ``build_source_links``
 * resolver and carried on ``row.source_links`` so the drawer's source-link row
 * renders the same links wherever a row is opened. ``system`` keys
 * ``getSystemIcon`` + the popup handler; ``kind`` is a coarse label
 * (sale/ledger/invoice/product) for future per-kind treatment.
 */
export type SourceLink = {
  system: string
  label: string
  url: string
  kind?: string
}

export type ChipState =
  | 'needs_review'
  | 'blocked'
  | 'resolved'
  | 'ignored'
  | 'empty'

export interface ResolvedState {
  state: ChipState
  count: number
  entries: IssuesChipEntry[]
  blockReason?: string
  // Open actionable entries already marked fixed (awaiting sync). Drives
  // the table chip's progress marker (see ``tableFixMarker``) + the
  // "N marked fixed" tooltip line.
  markedFixedCount: number
  // Marked-fixed entries a later sync still detected - escalates the marker
  // to the red-orange "fix not confirmed" state.
  stillDetectedCount: number
}

// Palette literals - matched against existing renderers in
// productRenderers.ts so the chip blends with the rest of the table.
export const WARNING_FG = '#ED6C02'
export const WARNING_BG = '#FFF4E5'
export const BLOCKED_FG = '#D32F2F'
export const BLOCKED_BG = '#FDECEA'
export const RESOLVED_FG = '#007F98'
export const IGNORED_FG = '#6B7280'
export const IGNORED_BG = '#F3F4F6'
export const DISABLED_FG = '#9CA3AF'

export const STATE_LABEL: Record<ChipState, string> = {
  needs_review: 'Needs review',
  blocked: 'Blocked',
  resolved: 'Recently resolved (last 30 days)',
  ignored: 'Ignored',
  empty: 'No issues',
}

/** Teal: the mark-fixed ACTION affordances (kebab "Mark fixed" / "Clear marked
 *  fixed", the "I've fixed all" button), the drawer's "Marked fixed" stamp, the
 *  table chip's awaiting pennant (via ``FIX_STATE_META``), and the timeline
 *  marked-fixed event. Teal = actioned, awaiting confirmation; never green
 *  (green/check = sync-confirmed resolved). */
export const MARKED_FIXED_FG = '#007F98'
/** Drawer "Marked fixed" stamp chrome (awaiting sync): hairline teal border on a
 *  very pale teal fill - reads as quiet metadata, never a second status pill. */
export const MARKED_FIXED_STAMP_BORDER = 'rgba(0, 127, 152, 0.45)'
export const MARKED_FIXED_STAMP_BG = 'rgba(0, 127, 152, 0.04)'
/** Failed-state stamp (struck through): muted slate-teal text on a hairline
 *  border + near-transparent fill, so the stamp visibly de-emphasises while the
 *  red-orange "Not confirmed" detail carries the alarm. */
export const MARKED_FIXED_MUTED_FG = '#7A9CA3'
export const MARKED_FIXED_MUTED_BORDER = 'rgba(0, 127, 152, 0.25)'
export const MARKED_FIXED_MUTED_BG = 'rgba(0, 127, 152, 0.02)'
/** Red-orange: the drawer row's "Not confirmed" detail text and the timeline
 *  fix-not-confirmed event. Sits between warning amber and error red. */
export const FIX_NOT_CONFIRMED_FG = '#E65100'
/** Clear RED for the "Failed check" state - the drawer's marked-fixed chip and
 *  the table chip's not-confirmed pennant share it so a row marked fixed but
 *  still flagged by a later sync reads the same on both surfaces. */
export const FIX_FAILED_FG = '#C62828'
export const FIX_FAILED_BORDER = 'rgba(198, 40, 40, 0.5)'

/** Marked-fixed copy. The stamp text is constant ("Marked fixed") across both
 *  states; a short status word follows it - the long explanation lives in the
 *  tooltip only. Kept here so every surface reads identically. */
export const MARKED_FIXED_STAMP_LABEL = 'Marked fixed'
export const AWAITING_SYNC_DETAIL = 'Pending next sync'
export const FIX_NOT_CONFIRMED_DETAIL = 'Failed check'

export type FixMarkerState = 'awaiting_sync' | 'not_confirmed'

/**
 * Static presentation for each marked-fixed state - the single source for the
 * filled glyph, fill colour and screen-reader label. The table chip's corner
 * pennant consumes all three; the drawer chip sources the colour from here too
 * (it keeps its own *outlined* glyph - a roomy outline chip reads better
 * outlined, where the 12px solid pennant needs a filled glyph). The hover copy
 * is NOT here - it depends on the entry/counts (see ``tableFixMarker`` /
 * ``fixMarker``). */
export const FIX_STATE_META: Record<
  FixMarkerState,
  { Icon: SvgIconComponent; color: string; ariaLabel: string }
> = {
  awaiting_sync: {
    Icon: AssignmentTurnedInIcon,
    color: MARKED_FIXED_FG,
    ariaLabel: 'Marked fixed, awaiting sync confirmation',
  },
  not_confirmed: {
    Icon: AssignmentLateIcon,
    color: FIX_FAILED_FG,
    ariaLabel: 'Fix not confirmed by sync',
  },
}

export interface FixMarker {
  state: FixMarkerState
  /** Stamp text - always "Marked fixed"; the state lives in the styling. */
  label: string
  /** Muted supporting detail, e.g. "waiting for sync confirmation". */
  detail: string
  /** Two-line hover detail: [what the sync saw / the state, who marked it +
   *  when]. The actor line is dropped when neither actor nor time is known. */
  tooltip: string[]
}

/** "Lee Haskings (Evo)" / "Lee Haskings" / "the Evo team" / null. Never an
 *  email - the backend only sends a name + team label. */
const markedFixedActor = (entry: IssuesChipEntry): string | null => {
  const name = entry.marked_fixed_by
  const team = entry.marked_fixed_by_team
  if (name) return team ? `${name} (${team})` : name
  return team ? `the ${team} team` : null
}

/** Relative "2h ago" label, or null when the timestamp is absent/unparseable. */
const relativeAgo = (iso?: string | null): string | null => {
  if (!iso) return null
  const { label } = formatRelativeTimestamp(iso)
  return label === '-' ? null : label
}

/**
 * Secondary marked-fixed PROGRESS marker for an OPEN entry, or null when the
 * entry is unmarked or closed. Single source for the drawer's under-title line
 * and the table chip's tiny side marker so icon / colour / copy / tooltip
 * never diverge:
 *  - awaiting_sync: amber, AssignmentTurnedInOutlined
 *  - not_confirmed: red-orange, AssignmentLateOutlined (a later sync still saw it)
 */
export const fixMarker = (entry: IssuesChipEntry): FixMarker | null => {
  if (!entry.marked_fixed || !isOpenEntry(entry)) return null
  const actor = markedFixedActor(entry)
  const markedAgo = relativeAgo(entry.marked_fixed_at)
  // Tooltip line 2: who marked it fixed and when. Omitted when neither known.
  const actorLine = actor
    ? `Marked fixed by ${actor}${markedAgo ? ` ${markedAgo}` : ''}`
    : markedAgo
      ? `Marked fixed ${markedAgo}`
      : null
  if (entry.fix_confirmation_state === 'still_detected') {
    const syncAgo = relativeAgo(entry.last_detected_at)
    const syncLine = syncAgo
      ? `Latest sync ${syncAgo} still detected this issue`
      : 'The latest sync still detected this issue'
    return {
      state: 'not_confirmed',
      label: MARKED_FIXED_STAMP_LABEL,
      detail: FIX_NOT_CONFIRMED_DETAIL,
      tooltip: actorLine ? [syncLine, actorLine] : [syncLine],
    }
  }
  return {
    state: 'awaiting_sync',
    label: MARKED_FIXED_STAMP_LABEL,
    detail: AWAITING_SYNC_DETAIL,
    tooltip: actorLine
      ? ['Waiting for sync confirmation', actorLine]
      : ['Waiting for sync confirmation'],
  }
}

/** Render the marker's 1-2 tooltip lines as stacked text (line 1 = what the
 *  sync saw / the state; line 2 = who marked it + when, dimmed). Shared by the
 *  drawer line and the table marker so the hover detail reads identically. */
export const fixMarkerTooltipNode = (lines: string[]): React.ReactNode =>
  React.createElement(
    'span',
    { style: { display: 'block', lineHeight: 1.45 } },
    lines.map((line, i) =>
      React.createElement(
        'span',
        { key: i, style: { display: 'block', opacity: i === 0 ? 1 : 0.78 } },
        line,
      ),
    ),
  )

/**
 * The table chip's marked-fixed PENNANT - a small corner overlay (top-left,
 * mirroring the note badge) that sources its icon + colour from
 * ``FIX_STATE_META``, so it never widens the Issues column. Aggregate state
 * across the row's marked-fixed entries.
 */
export interface TableFixMarker {
  state: FixMarkerState
  /** Short, screen-reader label for the pennant (count detail rides the tooltip). */
  ariaLabel: string
  /** Hover lines: count-based when more than one entry is marked fixed; the
   *  single entry's ``fixMarker`` lines when exactly one (so the chip's hover
   *  reads identically to the drawer). Not-confirmed leads when both exist. */
  tooltip: string[]
}

/**
 * Aggregate marked-fixed pennant state + hover for the table chip. Priority:
 * any still-detected entry -> not_confirmed; else any awaiting -> awaiting_sync;
 * else none. With exactly one marked-fixed entry the tooltip reuses that entry's
 * per-row ``fixMarker`` lines; with several it's count-based (not-confirmed first).
 */
export const tableFixMarker = (s: ResolvedState): TableFixMarker | null => {
  if (s.markedFixedCount === 0) return null

  // Exactly one marked-fixed entry: reuse its per-row fixMarker so the chip's
  // hover reads identically to the drawer ("Marked fixed by {actor} {ago}").
  if (s.markedFixedCount === 1) {
    const single = s.entries.find((e) => fixMarker(e))
    const fm = single ? fixMarker(single) : null
    if (fm) {
      return {
        state: fm.state,
        ariaLabel: FIX_STATE_META[fm.state].ariaLabel,
        tooltip: fm.tooltip,
      }
    }
  }

  // Several marked fixed (or the lookup missed): count-based lines.
  const awaiting = s.markedFixedCount - s.stillDetectedCount
  const notConfirmed = s.stillDetectedCount
  const lines: string[] = []
  if (notConfirmed > 0) {
    lines.push(
      `${notConfirmed} ${notConfirmed === 1 ? 'fix' : 'fixes'} still flagged by sync`,
    )
  }
  if (awaiting > 0) {
    lines.push(`${awaiting} marked fixed - pending sync confirmation`)
  }
  const state: FixMarkerState = notConfirmed > 0 ? 'not_confirmed' : 'awaiting_sync'
  return {
    state,
    ariaLabel: FIX_STATE_META[state].ariaLabel,
    tooltip: lines,
  }
}

export type StatusDetailKind =
  | 'awaiting_sync'
  | 'not_confirmed'
  | 'blocked'
  | 'needs_review'
  | 'ignored'
  | 'resolved'

/** The drawer row's uniform second-line detail. ``stamp`` flags the marked-fixed
 *  "[Marked fixed]" stamp (``struck`` when not-confirmed); ``lead`` is an
 *  emphasised prefix (e.g. "Blocked") and ``text`` trails it. ``tooltip`` is the
 *  hover detail. The component maps ``kind`` -> colour/weight. */
export interface StatusDetail {
  kind: StatusDetailKind
  stamp: boolean
  struck: boolean
  lead?: string
  text: string
  tooltip?: string[]
}

/**
 * Uniform second-row status detail for an issue row - ONE helper so every row
 * (not just marked-fixed) gets a consistent "what's the team done" line.
 * Priority: marked-fixed (still-detected -> awaiting via ``fixMarker``) ->
 * blocked -> ignored -> resolved -> needs-review default.
 *
 * Actor copy ("Blocked by {user}" / "Ignored by {user}") is intentionally NOT
 * built here - the entry envelope carries no block/ignore actor (only
 * ``marked_fixed_by``). Surface it once the backend includes the actor.
 */
/** "{verb} by {name} {ago}" hover line for a lifecycle action, dropping the
 *  parts that aren't known (no actor -> "{verb} {ago}"; nothing -> null). */
const actorActionTooltip = (
  verb: string,
  name?: string | null,
  iso?: string | null,
): string[] | undefined => {
  const who = name?.trim()
  const ago = relativeAgo(iso)
  if (who) return [`${verb} by ${who}${ago ? ` ${ago}` : ''}`]
  if (ago) return [`${verb} ${ago}`]
  return undefined
}

export const statusDetail = (entry: IssuesChipEntry): StatusDetail => {
  const fm = fixMarker(entry)
  if (fm) {
    return {
      kind: fm.state,
      stamp: true,
      struck: fm.state === 'not_confirmed',
      text: fm.detail,
      tooltip: fm.tooltip,
    }
  }
  if (entry.blocked) {
    const reason = entry.block_reason?.trim()
    return {
      kind: 'blocked',
      stamp: false,
      struck: false,
      lead: reason ? 'Blocked reason:' : undefined,
      text: reason ? ` ${reason}` : 'Blocked',
      // Hover reveals who blocked it + when.
      tooltip: actorActionTooltip('Blocked', entry.blocked_by, entry.blocked_at),
    }
  }
  if (entry.kind === 'ignored') {
    const reason = entry.ignore_reason?.trim()
    return {
      kind: 'ignored',
      stamp: false,
      struck: false,
      lead: reason ? 'Ignored reason:' : undefined,
      text: reason ? ` ${reason}` : 'Ignored',
      // Hover reveals who ignored it + when.
      tooltip: actorActionTooltip('Ignored', entry.ignored_by, entry.ignored_at),
    }
  }
  if (entry.kind === 'resolved') {
    // A row that was marked fixed and then sync-confirmed reads as the person's
    // win ("Fixed by Lee Haskings (Evo)"); a sync that cleared it on its own
    // reads as when it cleared.
    const actor = markedFixedActor(entry)
    if (entry.marked_fixed && actor) {
      return {
        kind: 'resolved',
        stamp: false,
        struck: false,
        text: `Fixed by ${actor}`,
      }
    }
    const ago = relativeAgo(entry.resolved_at)
    return {
      kind: 'resolved',
      stamp: false,
      struck: false,
      text: ago ? `Resolved ${ago}` : 'Resolved by sync',
    }
  }
  return {
    kind: 'needs_review',
    stamp: false,
    struck: false,
    text: 'No action recorded yet',
    tooltip: ['This issue is open and has not been actioned yet'],
  }
}

/** One status chip for the drawer row's status-detail line. Unlike
 *  ``statusDetail`` (which collapses to a single line), this returns the FULL
 *  set so a row that is both blocked AND marked-fixed shows both chips. */
export interface RowStatusChip {
  kind: FixMarkerState | 'blocked' | 'ignored'
  /** Bold lead, e.g. "Blocked" / "Marked fixed". */
  label: string
  /** Lighter " - {suffix}" - the reason (blocked/ignored) or state (marked fixed). */
  suffix: string | null
  /** Hover detail (reason leads for blocked/ignored, then who/when). */
  tooltip?: string[]
}

/**
 * Ordered status chips for a row. Blocked leads (it's the lifecycle status the
 * rail/icon already colour), then the marked-fixed progress (a blocked row can
 * also be marked fixed - both show). Ignored stands alone (a closed row can't be
 * marked fixed). Resolved / needs-review return none (resolved renders as text;
 * needs-review shows nothing). Reuses ``actorActionTooltip`` / ``fixMarker`` so
 * the copy + hover detail never diverge from ``statusDetail``.
 */
export const rowStatusChips = (entry: IssuesChipEntry): RowStatusChip[] => {
  const chips: RowStatusChip[] = []
  if (entry.blocked) {
    const reason = entry.block_reason?.trim()
    const actor = actorActionTooltip('Blocked', entry.blocked_by, entry.blocked_at)
    chips.push({
      kind: 'blocked',
      label: 'Blocked',
      suffix: reason || null,
      tooltip: reason ? [reason, ...(actor ?? [])] : actor,
    })
  }
  const fm = fixMarker(entry)
  if (fm) {
    chips.push({
      kind: fm.state,
      label: fm.label,
      suffix: fm.detail,
      tooltip: fm.tooltip,
    })
  }
  if (entry.kind === 'ignored') {
    const reason = entry.ignore_reason?.trim()
    const actor = actorActionTooltip('Ignored', entry.ignored_by, entry.ignored_at)
    chips.push({
      kind: 'ignored',
      label: 'Ignored',
      suffix: reason || null,
      tooltip: reason ? [reason, ...(actor ?? [])] : actor,
    })
  }
  return chips
}

/**
 * Per-tag lifecycle status type. Excludes `empty` (which is a chip-only
 * concept for the "no issues" placeholder) and is used by drawer +
 * admin-activity surfaces that work with a single concrete tag.
 */
export type LifecycleStatus = 'needs_review' | 'blocked' | 'resolved' | 'ignored'

/**
 * Short, capitalised labels for a single tag's lifecycle status.
 * Distinct from `STATE_LABEL` which carries the chip's longer phrasing
 * (e.g. "Recently resolved (last 30 days)"). Used by the drawer's
 * status chip and the admin Exception Activity table's Status column
 * so both surfaces show the same wording.
 */
export const STATUS_LABEL: Record<LifecycleStatus, string> = {
  needs_review: 'Needs review',
  blocked: 'Blocked',
  resolved: 'Resolved',
  ignored: 'Ignored',
}

/**
 * Tone mapping for the status chip. Imported from `chipPresets` so a
 * future palette change in either surface stays in one place.
 */
export const STATUS_TONE: Record<
  LifecycleStatus,
  'warning' | 'error' | 'success' | 'neutral'
> = {
  needs_review: 'warning',
  blocked: 'error',
  resolved: 'success',
  ignored: 'neutral',
}

/**
 * The weight-0 umbrella (the sales reconciliation roll-up) is a headline, not
 * a countable issue: drop it when specific codes already flag the object, but
 * keep it when it's the SOLE flag (date / client / line-item drift has no
 * discrete code of its own). Both the table chip (``resolveState``) and the
 * drawer (``actionableOpenErrors``) consume this so the per-object count never
 * disagrees across surfaces - and it matches the dashboard rollup, which
 * already excludes weight-0 via ``COALESCE(weight, 0) > 0``. A no-op for
 * non-sales domains, whose entries never carry ``is_umbrella``.
 */
export const actionableEntries = <T extends { is_umbrella?: boolean }>(
  entries: T[] | undefined,
): T[] => {
  const all = entries ?? []
  const specific = all.filter((e) => !e.is_umbrella)
  return specific.length > 0 ? specific : all
}

/**
 * Collapse same-code entries into ONE representative per code so the drawer
 * lists "one problem" even when it fired at several outlets - a no-op for
 * surfaces where every code is unique (each group has one member, returned as
 * is, so nothing about their rendering changes). For a real multi-outlet group
 * the representative is the first member with status aggregated (blocked if ANY
 * member is; marked-fixed only when ALL are; still-detected if ANY is), the
 * EARLIEST detection (the longest-standing outlet anchors the age), and the
 * ``outlet_*`` / ``member_tag_ids`` fields filled for the breakdown + the
 * grouped kebab. Order-preserving (first occurrence keeps the group's slot).
 */
export const groupEntriesByCode = (
  entries: IssuesChipEntry[],
): IssuesChipEntry[] => {
  const order: string[] = []
  const byCode = new Map<string, IssuesChipEntry[]>()
  for (const e of entries) {
    const list = byCode.get(e.code)
    if (list) list.push(e)
    else {
      byCode.set(e.code, [e])
      order.push(e.code)
    }
  }
  return order.map((code) => {
    const members = byCode.get(code) as IssuesChipEntry[]
    if (members.length === 1) return members[0]
    const base = members[0]
    const outletNames = members
      .map((m) => m.outlet_name)
      .filter((n): n is string => !!n)
    const memberTagIds = members
      .map((m) => m.tag_id)
      .filter((t): t is number => typeof t === 'number')
    const earliest =
      members
        .map((m) => m.detected_at ?? m.last_detected_at)
        .filter((d): d is string => !!d)
        .sort()[0] ?? base.detected_at
    const stillDetected = members.some(
      (m) => m.fix_confirmation_state === 'still_detected',
    )
    return {
      ...base,
      blocked: members.some((m) => m.blocked),
      marked_fixed: members.every((m) => m.marked_fixed),
      fix_confirmation_state: stillDetected
        ? 'still_detected'
        : base.fix_confirmation_state,
      detected_at: earliest,
      outlet_count: members.length,
      outlet_names: outletNames,
      member_tag_ids: memberTagIds,
    }
  })
}

/** An open (still-actionable) entry - anything not yet ignored or resolved.
 *  Blocked and marked-fixed rows are still "open" (awaiting a fix / sync). */
export const isOpenEntry = (e: IssuesChipEntry): boolean =>
  e.kind !== 'ignored' && e.kind !== 'resolved'

/** The row's anchor date - ALWAYS the original detection (how long the issue
 *  has existed), regardless of lifecycle state. The close timing for
 *  resolved/ignored rows lives in the status-detail line instead. Null when no
 *  detection timestamp is known. */
export interface EntryDateMeta {
  prefix: string
  iso: string
}
export const entryDateMeta = (e: IssuesChipEntry): EntryDateMeta | null => {
  const iso = e.detected_at ?? e.last_detected_at
  return iso ? { prefix: 'Detected', iso } : null
}

/**
 * Sort rank for the unified Issues list: Blocked -> Needs review ->
 * Marked fixed -> Ignored -> Resolved. Open buckets lead; a marked-fixed row
 * (still open, awaiting sync) sits just after plain needs-review; the closed
 * buckets trail. Blocked wins over marked-fixed when a row is both.
 */
export const entryStatusRank = (e: IssuesChipEntry): number => {
  if (e.blocked) return 0
  if (e.kind === 'ignored') return 3
  if (e.kind === 'resolved') return 4
  if (e.marked_fixed) return 2
  return 1
}

/** Stable status sort (Array.prototype.sort is stable), so same-rank rows keep
 *  their incoming order (open siblings before closed within a rank tie). Used
 *  for the closed-history section; the open list uses ``sortOpenByDetected``. */
export const sortEntriesByStatus = <T extends IssuesChipEntry>(
  entries: T[],
): T[] => [...entries].sort((a, b) => entryStatusRank(a) - entryStatusRank(b))

/**
 * Open-list order: oldest detection first, so the longest-standing issues lead
 * the Issues tab regardless of lifecycle state - blocked / marked-fixed rows no
 * longer jump the queue (the closed-history section still uses
 * ``sortEntriesByStatus``). Entries with no detection date sort last; the stable
 * sort keeps incoming order for exact ties.
 */
export const sortOpenByDetected = <T extends IssuesChipEntry>(
  entries: T[],
): T[] =>
  [...entries].sort((a, b) => {
    const aIso = entryDateMeta(a)?.iso
    const bIso = entryDateMeta(b)?.iso
    const av = aIso ? new Date(aIso).getTime() : NaN
    const bv = bIso ? new Date(bIso).getTime() : NaN
    const aBad = Number.isNaN(av)
    const bBad = Number.isNaN(bv)
    if (aBad && bBad) return 0
    if (aBad) return 1
    if (bBad) return -1
    return av - bv
  })

/**
 * Optimistic per-tag change applied in the drawer right after a mutation so the
 * Issues list reflects the new status immediately - the source ``row.errors``
 * only refetches when the drawer closes. Keyed by ``tag_id`` in the drawer.
 */
export type EntryOverride = Partial<
  Pick<
    IssuesChipEntry,
    'blocked' | 'block_reason' | 'marked_fixed' | 'fix_confirmation_state' | 'kind'
  >
>

export const resolveState = (
  errors: IssuesChipEntry[] | undefined,
  closed: IssuesChipEntry[] | undefined,
): ResolvedState => {
  // Collapse the umbrella before counting so the chip count/colour/tooltip
  // track the specific codes (see ``actionableEntries``).
  const open = actionableEntries(errors)
  if (open.length > 0) {
    const blockedPrimary = open.find((e) => e.primary && e.blocked)
    const anyBlocked = open.some((e) => e.blocked)
    const marked = open.filter((e) => e.marked_fixed)
    const markedFixedCount = marked.length
    const stillDetectedCount = marked.filter(
      (e) => e.fix_confirmation_state === 'still_detected',
    ).length
    // Main chip is lifecycle-only: blocked wins, else needs_review. Marked-fixed
    // is NOT a chip state - it surfaces separately as a progress marker (see
    // ``tableFixMarker``), so the chip never recolours teal/amber for it.
    return {
      state: anyBlocked ? 'blocked' : 'needs_review',
      // Occurrence count: a product negative at 4 outlets reads "4" (each
      // negative outlet is an issue), matching the backend ``open_issues`` and
      // the drawer's "· 4 outlets" badge on the one grouped row.
      count: open.length,
      entries: open,
      blockReason: blockedPrimary?.block_reason,
      markedFixedCount,
      stillDetectedCount,
    }
  }
  const closedOpen = actionableEntries(closed)
  if (closedOpen.length > 0) {
    const allIgnored = closedOpen.every((e) => e.kind === 'ignored')
    return {
      state: allIgnored ? 'ignored' : 'resolved',
      count: closedOpen.length,
      entries: closedOpen,
      markedFixedCount: 0,
      stillDetectedCount: 0,
    }
  }
  return {
    state: 'empty',
    count: 0,
    entries: [],
    markedFixedCount: 0,
    stillDetectedCount: 0,
  }
}

export const iconFor = (state: ChipState): React.ReactNode => {
  if (state === 'needs_review') return React.createElement(WarningAmberIcon)
  if (state === 'blocked') return React.createElement(BlockIcon)
  if (state === 'resolved') return React.createElement(CheckCircleOutlineIcon)
  if (state === 'ignored') return React.createElement(VisibilityOffIcon)
  return null
}

export const chipSxFor = (state: ChipState): Record<string, any> => {
  const base = {
    // 26px (not the MUI-small 22) so the icon + digit have breathing room and the
    // overhanging corner badges (note / fix pennant) don't crowd them. The product
    // tables' 2-line cell already drives row height, so this costs no rows-per-screen.
    height: 26,
    // Slightly wider than the icon+digit pair so the count and the corner badges
    // never visually crowd each other.
    minWidth: 38,
    fontSize: '0.75rem',
    fontWeight: 700,
    '& .MuiChip-label': { px: 1 },
    // ml matches the label's 8px right inset so the icon sits clear of the
    // pill's rounded left edge (4px crowded it into the curve); mr keeps the
    // icon-to-digit gap tight.
    '& .MuiChip-icon': { ml: 1, mr: -0.25, fontSize: 16 },
    cursor: 'pointer',
  }
  if (state === 'empty') {
    // Dash chip for a row with no open issues. Matches the populated
    // chips' rendered width (icon + digit ≈ 40px; the dash has no icon so
    // the minWidth floor does the work) and keeps a visible-but-secondary
    // tone - ignored-grey text, lighter border, soft hover tint. Always
    // clickable: the drawer still holds the closed history / notes.
    return {
      ...base,
      minWidth: 40,
      fontWeight: 600,
      color: IGNORED_FG,
      borderColor: DISABLED_FG,
      backgroundColor: 'transparent',
      '&:hover': { backgroundColor: IGNORED_BG, borderColor: IGNORED_FG },
    }
  }
  if (state === 'needs_review') {
    return {
      ...base,
      color: '#FFFFFF',
      backgroundColor: WARNING_FG,
      borderColor: WARNING_FG,
      '& .MuiChip-icon': {
        ...base['& .MuiChip-icon'],
        color: '#FFFFFF !important',
      },
    }
  }
  if (state === 'blocked') {
    return {
      ...base,
      color: '#FFFFFF',
      backgroundColor: BLOCKED_FG,
      borderColor: BLOCKED_FG,
      '& .MuiChip-icon': {
        ...base['& .MuiChip-icon'],
        color: '#FFFFFF !important',
      },
    }
  }
  if (state === 'resolved') {
    return {
      ...base,
      color: RESOLVED_FG,
      borderColor: RESOLVED_FG,
      backgroundColor: 'transparent',
      '& .MuiChip-icon': {
        ...base['& .MuiChip-icon'],
        color: `${RESOLVED_FG} !important`,
      },
    }
  }
  // ignored
  return {
    ...base,
    color: IGNORED_FG,
    borderColor: IGNORED_FG,
    backgroundColor: IGNORED_BG,
    '& .MuiChip-icon': {
      ...base['& .MuiChip-icon'],
      color: `${IGNORED_FG} !important`,
    },
  }
}

export const headerColorFor = (state: ChipState): string => {
  if (state === 'blocked') return BLOCKED_FG
  if (state === 'needs_review') return WARNING_FG
  if (state === 'resolved') return RESOLVED_FG
  if (state === 'ignored') return IGNORED_FG
  return DISABLED_FG
}

/**
 * A single entry's lifecycle status for drawer row rendering (rail colour,
 * severity icon, status pill, kebab items). Blocked wins; closed kinds map
 * to their bucket; everything else is needs_review. Marked-fixed is NOT a
 * status here - it renders as a secondary progress marker (see ``fixMarker``).
 */
export const resolveEntryStatus = (entry: IssuesChipEntry): LifecycleStatus => {
  if (entry.blocked) return 'blocked'
  if (entry.kind === 'ignored') return 'ignored'
  if (entry.kind === 'resolved') return 'resolved'
  return 'needs_review'
}

/**
 * Foreground colour per lifecycle status - the drawer row's slim left rail,
 * severity icon and status pill all source from this so blocked reads red,
 * needs-review amber, resolved teal (sync-confirmed only) and ignored grey
 * on every surface.
 */
export const STATUS_FG: Record<LifecycleStatus, string> = {
  needs_review: WARNING_FG,
  blocked: BLOCKED_FG,
  resolved: RESOLVED_FG,
  ignored: IGNORED_FG,
}

export const ariaLabelFor = (s: ResolvedState): string => {
  if (s.state === 'empty') return 'No issues'
  if (s.state === 'needs_review') return `${s.count} open issues, needs review`
  if (s.state === 'blocked') {
    return s.blockReason
      ? `${s.count} open issues, blocked: ${s.blockReason}`
      : `${s.count} open issues, blocked`
  }
  if (s.state === 'resolved') return `${s.count} recently resolved issues`
  if (s.state === 'ignored') return `${s.count} ignored issues`
  return ''
}

/**
 * Multi-line tooltip content for the IssuesChip - one string per line.
 * Drops zero/absent parts so we never say "0 notes" or render a blank
 * "last sync" line. The block reason stays a separately-styled trailing
 * line rendered by the caller.
 *
 * Open view shape:
 *   2 open issues
 *   1 blocked
 *   1 marked fixed - awaiting sync confirmation
 *   Oldest open: 54d
 *   Last confirmed by sync: 2h ago
 *   Notes: 1
 *   Click to inspect
 *
 * Closed view shape:
 *   5 closed issues in the last 30 days
 *   5 resolved by sync
 *   Last resolved: 25 May 2026, 5:31 PM
 *   Click to inspect history
 */
export const tooltipLines = (
  s: ResolvedState,
  noteCount: number,
  lastSyncAt?: string | null,
): string[] => {
  const lines: string[] = []
  const notesLine = () => {
    if (noteCount > 0) lines.push(`Notes: ${noteCount}`)
  }

  if (s.state === 'resolved' || s.state === 'ignored') {
    const resolvedCount = s.entries.filter((e) => e.kind === 'resolved').length
    const ignoredCount = s.entries.filter((e) => e.kind === 'ignored').length
    lines.push(
      `${s.count} closed ${s.count === 1 ? 'issue' : 'issues'} in the last 30 days`,
    )
    if (resolvedCount > 0) lines.push(`${resolvedCount} resolved by sync`)
    if (ignoredCount > 0) lines.push(`${ignoredCount} ignored`)
    const lastResolved = s.entries
      .map((e) => e.resolved_at)
      .filter((t): t is string => Boolean(t))
      .sort()
      .pop()
    if (lastResolved) {
      lines.push(`Last resolved: ${formatRelativeTimestamp(lastResolved).title}`)
    }
    notesLine()
    lines.push('Click to inspect history')
    return lines
  }

  if (s.state === 'empty') {
    notesLine()
    lines.push('Click to inspect')
    return lines
  }

  // Open states: needs_review / blocked.
  lines.push(`${s.count} open ${s.count === 1 ? 'issue' : 'issues'}`)
  const blockedCount = s.entries.filter((e) => e.blocked).length
  if (blockedCount > 0) lines.push(`${blockedCount} blocked`)
  const marked = s.entries.filter((e) => e.marked_fixed)
  const stillDetected = marked.filter(
    (e) => e.fix_confirmation_state === 'still_detected',
  ).length
  if (marked.length > 0) {
    lines.push(`${marked.length} marked fixed - pending sync confirmation`)
  }
  if (stillDetected > 0) {
    lines.push(
      `${stillDetected} ${stillDetected === 1 ? 'fix' : 'fixes'} still flagged by sync`,
    )
  }
  const oldestOpen = s.entries
    .map((e) => e.detected_at)
    .filter((t): t is string => Boolean(t))
    .sort()[0]
  const oldestDays = getDaysOpen(oldestOpen)
  if (oldestDays !== null) {
    lines.push(oldestDays === 0 ? 'Oldest open: today' : `Oldest open: ${oldestDays}d`)
  }
  if (lastSyncAt) {
    lines.push(`Last confirmed by sync: ${formatRelativeTimestamp(lastSyncAt).label}`)
  }
  notesLine()
  lines.push('Click to inspect')
  return lines
}
